import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';
import {
  TableV2CellDirective,
  TableV2EmptyDirective,
  TableV2HeaderDirective,
  TableV2RowAfterDirective,
} from './table-v2.directives';
import { TableV2Action, TableV2Column, TableV2Layout, TableV2Size, TableV2Tone } from './table-v2.interface';

/** Clicks on these do their own thing, not the row click */
const INTERACTIVE = 'button, a, input, select, textarea, label, [role="button"], [role="combobox"], .p-select';

const ACTION_TONES: Record<TableV2Tone, { idle: string; active: string }> = {
  default: { idle: 'text-slate-400 hover:bg-slate-100 hover:text-slate-700', active: 'bg-slate-100 text-slate-700' },
  success: {
    idle: 'text-slate-400 hover:bg-emerald-50 hover:text-emerald-700',
    active: 'bg-emerald-50 text-emerald-700',
  },
  danger: { idle: 'text-slate-400 hover:bg-rose-50 hover:text-rose-600', active: 'bg-rose-50 text-rose-600' },
};

/**
 * Table of the app: columns from a config, cells from `appTableV2Cell` templates, row actions, row click,
 * loading rows and an empty state. Header alignment and row borders are forced with `!`, as the global
 * Bootstrap reboot overrides them otherwise.
 */
@Component({
  selector: 'app-table-v2',
  templateUrl: './table-v2.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: {
    '[class.flex]': 'isPage',
    '[class.min-h-0]': 'isPage',
    '[class.flex-1]': 'isPage',
    '[class.flex-col]': 'isPage',
    '[class.block]': '!isPage',
  },
})
export class TableV2Component<T> implements AfterContentInit {
  @Input() columns: TableV2Column<T>[] = [];
  @Input() rows: T[] = [];
  /** Identity of a row for re-rendering, the row itself by default */
  @Input() trackBy: (row: T) => unknown = (row) => row;
  @Input() loading = false;
  @Input() set skeletonRows(count: number) {
    this.skeleton = Array.from({ length: count }, (_, index) => index);
  }
  @Input() layout: TableV2Layout = 'page';
  @Input() size: TableV2Size = 'md';
  /** Width under which the table scrolls sideways, e.g. `min-w-[960px]` */
  @Input() minWidth = '';
  @Input() rowClass?: (row: T) => string;
  @Input() actions: TableV2Action<T>[] = [];
  /** Width of the actions column */
  @Input() actionsClass = 'w-28';
  @Input() emptyIcon = '';
  /** Translation key */
  @Input() emptyText = '';
  @Output() rowClick = new EventEmitter<T>();

  @ContentChildren(TableV2CellDirective) private cellDirectives?: QueryList<TableV2CellDirective<T>>;
  @ContentChildren(TableV2HeaderDirective) private headerDirectives?: QueryList<TableV2HeaderDirective>;
  @ContentChild(TableV2RowAfterDirective) protected rowAfter?: TableV2RowAfterDirective<T>;
  @ContentChild(TableV2EmptyDirective) protected empty?: TableV2EmptyDirective;

  private readonly destroyRef = inject(DestroyRef);

  protected skeleton = [0, 1, 2, 3, 4];
  protected cellTemplates: Record<string, TemplateRef<unknown>> = {};
  protected headerTemplates: Record<string, TemplateRef<unknown>> = {};

  get isPage(): boolean {
    return this.layout === 'page';
  }

  get colspan(): number {
    return this.columns.length + (this.actions.length ? 1 : 0);
  }

  ngAfterContentInit(): void {
    this.collectTemplates();
    // Templates inside @if / @for of the parent come and go with them
    merge(this.cellDirectives!.changes, this.headerDirectives!.changes)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.collectTemplates());
  }

  /** `right` aligns a header only with `!`: the Bootstrap reboot aligns `th` itself */
  protected columnClasses(column: TableV2Column<T>, first: boolean, row?: T): string {
    const align = column.align && column.align !== 'left' ? `${row ? '' : '!'}text-${column.align}` : '';
    const padding = column.padding ?? (this.size === 'sm' ? 'px-2' : first ? 'px-4' : 'px-3');
    const cellClass =
      row !== undefined && typeof column.cellClass === 'function' ? column.cellClass(row) : column.cellClass;
    return [
      padding,
      this.size === 'md' ? (row ? 'py-2' : 'py-3') : row ? 'py-1.5' : 'py-2',
      align,
      column.nowrap ? 'whitespace-nowrap' : '',
      column.class ?? '',
      row !== undefined && typeof cellClass === 'string' ? cellClass : '',
    ].join(' ');
  }

  protected rowClasses(row: T): string {
    return `${this.rowClick.observed ? 'cursor-pointer hover:bg-slate-50' : ''} ${this.rowClass?.(row) ?? ''}`;
  }

  protected actionsPadding(): string {
    return this.size === 'md' ? 'px-3 py-2' : 'px-2 py-1.5';
  }

  protected cellValue(column: TableV2Column<T>, row: T): string | number {
    const value = column.value
      ? column.value(row)
      : (row as Record<string, string | number | null | undefined>)[column.key];
    return value === null || value === undefined || value === '' ? '—' : value;
  }

  protected actionClasses(action: TableV2Action<T>, row: T): string {
    const tone = ACTION_TONES[action.tone ?? 'default'];
    return action.active?.(row) ? tone.active : tone.idle;
  }

  /** An action field that is a value or depends on the row */
  protected resolve<V extends string>(value: V | ((row: T) => V), row: T): V {
    return typeof value === 'function' ? value(row) : value;
  }

  protected runAction(action: TableV2Action<T>, row: T, event: MouseEvent): void {
    event.stopPropagation();
    action.click(row);
  }

  protected onRowClick(row: T, event: MouseEvent): void {
    const interactive = (event.target as HTMLElement | null)?.closest(INTERACTIVE);
    if (this.rowClick.observed && !(interactive && (event.currentTarget as HTMLElement).contains(interactive))) {
      this.rowClick.emit(row);
    }
  }

  private collectTemplates(): void {
    const byKey = (
      directives?: QueryList<{ key: string; template: TemplateRef<unknown> }>,
    ): Record<string, TemplateRef<unknown>> =>
      Object.fromEntries((directives?.toArray() ?? []).map((directive) => [directive.key, directive.template]));
    this.cellTemplates = byKey(this.cellDirectives);
    this.headerTemplates = byKey(this.headerDirectives);
  }
}
